//@flow
import _ from 'lodash';
import ArrayUtil from './ArrayUtil';
import StringUtil from './StringUtil';
import HashMap from '../protocol/HashMap';
import GetMetadata from './../queries/metadata/GetMetadata';
import { AssetAiData } from '../queries/metadata/AssetAiData';
import { AssetAiKeyframeMetaData } from '../queries/metadata/AssetAiDataModel';
import { PROP_NAME_TO_PROP_INFO } from '../store/Constants';

import {
  AiMetadataValueMapArray,
  EditMetadataMsg,
  EditMetadataRes,
  FuidRrnInfo,
  GetMetadataQuery,
  GetMetadataResp,
  KeyFrameMetadataEntries,
  KeyFrameMetadataEntry,
  MessageMap,
  RepoItem,
  ZmCollection,
  FuidBidRrnWithAssetAiData,
  AssetAiDataWebclient,
  ValueList,
  TimeCode,
  DeleteTimelineQuery,
  DeleteTimelineMessage,
  DelId2KeyValPairList,
  DeleteTimelineResponse
} from '../autogenerated/ZoomBridge';
import { parseBool } from './BoolUtil';
import FileInfoUtil from './FileInfoUtil';
import EditMetadata from '../queries/metadata/EditMetadata';
import Store from '../store/Store';
import {
  AI_METADATA,
  KEYFRAME_METADATA,
  SELECTED_ASSET_AI_METADATA,
  SELECTED_ASSET_METADATA,
  ACTIVE_TIMELINE_NODE
} from '../store/Constants';
import AssetAiDataModel from '../queries/metadata/AssetAiDataModel';
import t from '../i18n/I18n';

export type GroupedMetadata = {
  [string]: HashMap
};

const debug = require('../debug/debug')('wc:util:MetadataUtil');

export default class MetadataUtil {
  static ASSET_SIZE_KEY = 'ASSET_SIZE';
  static ASSET_ID_KEY = 'ASSET_ID';
  static FIRP_ASSET_SIZE = 'FIPR_' + t('ASSET_SIZE');
  static FIPR_ASSET_ID = 'FIPR_' + t('ASSET_ID');

  static ASSET_ID_MD_PROP = 'FIPR_Asset ID';
  static FILE_PATH_MD_PROP = 'FIPR_File Path';
  static FILE_SIZE_MD_PROP = 'FIPR_File Size';
  static AUTHOR_MD_PROP = 'FIPR_Author';
  static REPO_REVISION_MD_PROP = 'FIPR_Repo Revision';
  static COMMENT_MD_PROP = 'FIPR_Comment';
  static REVISION_SIZE_MD_PROP = 'FIPR_Revision Size';

  static FIPR_KEY = 'FIPR';
  static AI_DATA_GROUP_CODE = 'AIDA';
  static AI_DATA_GROUP_CODE_PREFIX = 'AIDA_';

  static MULTIPLE_VALUES = '[MULTIPLE_VALUES]';
  static DD_MMM_FORMAT = 'DD/MMM/YYYY';
  static BLOCALED_METADATA_LIST = [
    'AIDA_logo',
    'AIDA_transcription',
    'AIDA_face',
    'AIDA_celeb',
    'AIDA_text',
    'AIDA_object',
    'FIPR_Preview Fuid',
    'FIPR_Source Fuid'
  ];
  static BLOCKED_METADATA_LIST_ADVANCE = [
    // 'AIDA_face',
    'FIPR_Preview Fuid',
    'FIPR_Source Fuid',
    MetadataUtil.FIPR_ASSET_ID,
    MetadataUtil.FIRP_ASSET_SIZE
  ];

  static DISABLED_METADATA_LIST_ADVANCE = []; //['AIDA_face'];

  static getPropInfo(fullname: string) {
    if (window.gs.propName2PropInfoMap) {
      return window.gs.propName2PropInfoMap.get(fullname);
    }
    return null;
  }

  static getPropInfoFromGroupAndPropId(groupCode: string, propId: string) {
    return MetadataUtil.getPropInfo(groupCode + +'_' + propId);
  }

  static splitGroupCodeAndPropName(fullname: string): { groupCode: string, propId: string } {
    const groupCode = fullname.substr(0, 4);
    const propId = fullname.substr(5);
    return { groupCode: groupCode, propId: propId };
  }

  static ConvertToGroup2PropObject(propId2ValueMap: HashMap): GroupedMetadata {
    let groups = {};
    _.each(propId2ValueMap.toArray(), (item) => {
      const { groupCode, propId } = MetadataUtil.splitGroupCodeAndPropName(item.key);
      if (groups[groupCode] === undefined) groups[groupCode] = new HashMap();
      groups[groupCode].put(propId, item.value);
    });
    return groups;
  }

  static findKeyInMetadataSpec(groupKey: string = ''): ?string {
    const metadataInfo = Store.get(PROP_NAME_TO_PROP_INFO);
    if (metadataInfo) {
      const groupInfo = metadataInfo.get(groupKey);
      if (groupInfo) {
        const dataGroup = groupInfo.getUserCanType();
        return dataGroup;
      }
    }
    return null;
  }

  static getMetadataValueType(key: string, groupCode: string) {
    const fielditem = groupCode + '_' + key;
    const metadataType = MetadataUtil.findKeyInMetadataSpec(fielditem);
    return metadataType;
  }

  static convertAiMetadataIntoGroup(aiMetadata: HashMap) {
    const map: HashMap = new HashMap();
    aiMetadata.forEach((key) => {
      const aiMetadataValues = aiMetadata.get(key);
      if (aiMetadataValues.length === 1) {
        const aiMetadataValue = aiMetadataValues[0];
        const group = new HashMap();
        const key: string = Object.keys(aiMetadataValue)[0];
        const value: string = aiMetadataValue[key];
        group.put(key, value);
        map.put(key, group);
      } else {
        const groups = {};
        _.each(aiMetadataValues, (aiMetadataValue, index) => {
          const key: string = Object.keys(aiMetadataValue)[0];
          const value: string = aiMetadataValue[key];
          if (groups[key + index + 1] === undefined) groups[key + index + 1] = new HashMap();
          groups[key + index + 1].put(key, value);
        });
        map.put(key, groups);
      }
    });
    return map;
  }

  static collateEditablePropsInAssets(assets: RepoItem[]) {
    let groupCode2PropIdMetadataValueMap = null;
    if (ArrayUtil.isNotEmpty(window.gs.editableMetadataProps)) {
      groupCode2PropIdMetadataValueMap = {};

      _.each(window.gs.editableMetadataProps, (fullPropId) => {
        const { groupCode, propId } = MetadataUtil.splitGroupCodeAndPropName(fullPropId);

        let __currentMetadataValue;
        let __loopingOverFirstAsset = true;
        for (let asset of assets) {
          const metadataMap = asset.getMetadataMap();
          const currentAssetMDValue = metadataMap.get(fullPropId);
          if (__currentMetadataValue === undefined) {
            if (StringUtil.isNotEmpty(currentAssetMDValue)) {
              if (__loopingOverFirstAsset) __currentMetadataValue = currentAssetMDValue;
              else {
                __currentMetadataValue = MetadataUtil.MULTIPLE_VALUES;
              }
            }
          } else if (__currentMetadataValue !== currentAssetMDValue) {
            __currentMetadataValue = MetadataUtil.MULTIPLE_VALUES;
            break;
          }
          __loopingOverFirstAsset = false;
        }
        if (groupCode2PropIdMetadataValueMap[groupCode] === undefined)
          groupCode2PropIdMetadataValueMap[groupCode] = new HashMap();
        groupCode2PropIdMetadataValueMap[groupCode].put(propId, __currentMetadataValue);
      });
    }
    return groupCode2PropIdMetadataValueMap;
  }

  static getCollectionAssetMetadata(repoItem: RepoItem) {
    let assetInfo: FuidRrnInfo = new FuidRrnInfo();
    assetInfo.setBid(repoItem.getBid());
    assetInfo.setFuid(repoItem.getFuid());
    assetInfo.setRrn(repoItem.getRrn());
    let assetInfoArray = [];
    assetInfoArray.push(assetInfo);

    new GetMetadata(assetInfoArray, false).run().then(
      function(resp: GetMetadataResp) {
        let messageMap: MessageMap = resp.getFuid2MetadataMap()
          ? resp.getFuid2MetadataMap().get(repoItem.getFuid())
          : null;
        if (messageMap && messageMap.getProperties()) {
          repoItem.setMetadataMap(messageMap.getProperties());
          MetadataUtil.addAssetIdInMetadataMapOfAsset([repoItem]);
        }
      },
      function(error) {
        console.log('Error trying to fetch metadata of the asset: ', repoItem.getFuid(), ', ', error);
      }
    );
  }

  static Collection = {
    isExpired(collectionOrMetadata: ZmCollection | HashMap) {
      let metadata;

      if (collectionOrMetadata instanceof ZmCollection) {
        const collection = collectionOrMetadata;
        metadata = collection.getMetadata();
      } else if (collectionOrMetadata instanceof HashMap) {
        metadata = collectionOrMetadata;
      } else {
        return false;
      }

      const isExpired = metadata.get('IS_EXPIRED');
      if (isExpired !== undefined) {
        return parseBool(isExpired);
      }

      const dueDate = parseInt(metadata.get('DUE_DATE'), 10);
      if (!isNaN(dueDate)) {
        return dueDate < Date.now();
      }

      return false;
    }
  };

  static addAssetIdInMetadataMapOfAsset(assets: RepoItem[]) {
    assets = assets || window.gs.assets || [];
    assets.forEach((asset) => {
      asset._metadataMap.put(MetadataUtil.ASSET_ID_MD_PROP, asset.getFuid());

      if (asset.getFrn() > 0) {
        asset._metadataMap.put(
          MetadataUtil.FILE_SIZE_MD_PROP,
          StringUtil.prettyBytes(asset.getBasicFileAttributes().getLength())
        );
        asset._metadataMap.put(
          MetadataUtil.REVISION_SIZE_MD_PROP,
          StringUtil.updateDeltaPercent(asset.getBasicFileAttributes().getLength(), asset.getDeltaSize())
        );
      } else {
        asset._metadataMap.put(MetadataUtil.FILE_SIZE_MD_PROP, '');
        asset._metadataMap.put(MetadataUtil.REVISION_SIZE_MD_PROP, '');
      }

      asset._metadataMap.put(MetadataUtil.FILE_PATH_MD_PROP, asset.getRemotePath());
      asset._metadataMap.put(MetadataUtil.REPO_REVISION_MD_PROP, asset.getRrn());
      asset._metadataMap.put(MetadataUtil.AUTHOR_MD_PROP, asset.getAuthor());
      asset._metadataMap.put(MetadataUtil.COMMENT_MD_PROP, asset.getComment());
    });
  }

  static getKeyFrameMetadata(asset: RepoItem): Promise<KeyFrameMetadataEntry[]> {
    const query: GetMetadataQuery = new GetMetadata([FileInfoUtil.createFuidRrnInfo(asset)], false);
    return new Promise((resolve, reject) => {
      query
        .run()
        .then((resp) => {
          debug('[getKeyFrameMetadata()] resp:', resp);
          const keyFrameMetadataEntries: KeyFrameMetadataEntries = resp
            .getFuid2keyFrameMetadataEntryList()
            .get(asset.getFuid());

          debug('[getKeyFrameMetadata()] keyFrameMetadataEntries: ', keyFrameMetadataEntries);
          if (!keyFrameMetadataEntries) {
            return resolve([]);
          }

          const keyFrameMetadataList: KeyFrameMetadataEntry[] = keyFrameMetadataEntries.getKeyFrameMetadataEntryList();
          debug('[getKeyFrameMetadata()] keyFrameMetadataList:', keyFrameMetadataList);
          resolve(keyFrameMetadataList || []);
        })
        .catch((err) => {
          debug('[getKeyFrameMetadata()] error: ', err);
          reject();
        });
    });
  }

  /**
   * Add/Edit/Delete KeyFrame Metadata
   * For Add:- send id as -1(or nay negative number)
   * For Edit:- send id as received from server
   * For Delete:- Send metadata value as null and id as received from server
   * @param asset
   * @param keyFrameMetadataEntryList
   * @return {Promise}
   */

  static editKeyFrameMetadata(
    asset: RepoItem,
    keyFrameMetadataEntryList: Array<KeyFrameMetadataEntry>
  ): Promise<EditMetadataRes> {
    const msg: EditMetadataMsg = new EditMetadataMsg();
    msg.setAssetsInfo([FileInfoUtil.createFuidRrnInfo(asset)]);
    msg.setKeyFrameMetadataEntryList(keyFrameMetadataEntryList);
    const query: EditMetadata = new EditMetadata(msg);

    return new Promise(function(onSuccess, onError) {
      query.run().then(
        (resp) => {
          if (resp) {
            debug('[editKeyFrameMetadata()] resp: ', resp);
            onSuccess(resp);
          }
        },
        (error) => {
          if (error) {
            debug('[editKeyFrameMetadata()] error: ', error);
            onError(error);
          }
        }
      );
    });
  }

  static getAiMetadata(asset: RepoItem): Promise<Map<String, AiMetadataValueMapArray>> {
    const query: GetMetadata = new GetMetadata([FileInfoUtil.createFuidRrnInfo(asset)], false);
    query.setAudioOrVideo(FileInfoUtil.isAudioOrVideo(asset)).setFetchAiData(true);
    return new Promise((resolve, reject) => {
      query
        .run()
        .then((resp) => {
          debug('[getAiMetadata()] resp:', resp);
          const aiMetadataValueMap: Map<String, AiMetadataValueMapArray> = resp.getAiMetadataMap();
          debug('[getAiMetadata()] aiMetadataValueMap: ', aiMetadataValueMap);
          if (!aiMetadataValueMap) {
            return resolve(new Map());
          }
          resolve(aiMetadataValueMap);
        })
        .catch((err) => {
          debug('[getAiMetadata()] error: ', err);
          reject(err);
        });
    });
  }

  /**
   * Delete Single KeyFrame Entry
   * @param startTime
   * @param endTime
   * @param id
   * @param key
   */

  static deleteKeyFrameEntry(asset: RepoItem, nodeContainer: Map<Object>): Promise<EditMetadataRes> {
    const entries = [];
    if (nodeContainer.size) {
      nodeContainer.forEach((node) => {
        const entry = new KeyFrameMetadataEntry();
        const metadataMap = new HashMap();
        metadataMap.put(node.key, null);
        entry.setEndTime(node.stopTimeMs);
        entry.setStartTime(node.startTimeMs);
        entry.setId(node.id);
        entry.setMetadataMap(metadataMap);
        entries.push(entry);
      });
    } else {
      //node Container is empty
    }
    return new Promise(function(onSuccess, onError) {
      MetadataUtil.editKeyFrameMetadata(asset, entries).then(
        (resp) => {
          if (resp) {
            debug('[deleteKeyFrameEntry()] resp: ', resp);
            onSuccess(resp);
          }
        },
        (error) => {
          debug('[deleteKeyFrameEntry()] error: ', error);
          onError(error);
        }
      );
    });
  }

  static deleteKeyFrameMetadata(
    asset: RepoItem,
    delId2KeyValPairList: Array<DelId2KeyValPairList>
  ): Promise<DeleteTimelineResponse> {
    const msg: DeleteTimelineMessage = new DeleteTimelineMessage();
    msg.setAssetsInfo([FileInfoUtil.createFuidRrnInfo(asset)]);
    msg.setDelId2KeyValPairList(delId2KeyValPairList);
    const query: DeleteTimelineQuery = new DeleteTimelineQuery();
    query.message = msg;

    return new Promise(function(onSuccess, onError) {
      query.run().then(
        (resp) => {
          if (resp) {
            debug('[deleteKeyFrameMetadata()] resp: ', resp);
            onSuccess(resp);
          }
        },
        (error) => {
          if (error) {
            debug('[deleteKeyFrameMetadata()] error: ', error);
            onError(error);
          }
        }
      );
    });
  }
  static deleteTimelineRow(asset: RepoItem, nodeContainer: Map<Object>): Promise<DeleteTimelineResponse> {
    const entries = [];
    const entry = new DelId2KeyValPairList();
    entry.setKey(nodeContainer.values().next().value.key);
    entry.setValue(' ');
    entries.push(entry);

    return new Promise(function(onSuccess, onError) {
      MetadataUtil.deleteKeyFrameMetadata(asset, entries).then(
        (resp) => {
          if (resp) {
            debug('[deleteTimelineRow()] resp: ', resp);
            onSuccess(resp);
          }
        },
        (error) => {
          debug('[deleteTimelineRow()] error: ', error);
          onError(error);
        }
      );
    });
  }

  /**
   * Delete Multiple KeyFrame Entries
   * @param asset
   * @param ids Id corresponding to each key need to be deleted e.g. [1,1,2,2]
   * @param keys key need to be deleted e.g. ['IPTC_Description','IPTC_Scene','IPTC_City','IPTC_Country']
   * @return {Promise}
   */

  static deleteMultipleKeyFrameEntries(
    asset: RepoItem,
    ids: Array<number>,
    keys: Array<string>
  ): Promise<EditMetadataRes> {
    const entries = [];
    if (ids.length === keys.length) {
      for (let iter = 0; iter < ids.length; iter++) {
        const entry = new KeyFrameMetadataEntry();
        entry.setId(ids[iter]);
        entry.setStartTime(-1);
        entry.setEndTime(-1);
        const map = new HashMap();
        map.put(keys[iter], null);
        entries.push(entry);
      }
      return new Promise(function(onSuccess, onError) {
        MetadataUtil.editKeyFrameMetadata(asset, entries).then(
          (resp) => {
            if (resp) {
              debug('[deleteMultipleKeyFrameEntries()] resp: ', resp);
              onSuccess(resp);
            }
          },
          (error) => {
            debug('[deleteMultipleKeyFrameEntries()] error: ', error);
            onError(error);
          }
        );
      });
    } else {
      debug('[deleteMultipleKeyFrameEntries()] error: Length of Array of ids is not equal to Length of Array of keys');
    }
  }

  static hasTimelineMetadata() {
    const aimd: HashMap = Store.get(AI_METADATA);
    const kfmd: Object[] = Store.get(KEYFRAME_METADATA);
    return Boolean((aimd && aimd.size && aimd.size()) || (kfmd && kfmd.length));
  }

  /**
   * Transforms aiMetadata and keyframeMetadata into the format the timeline originally expected,
   * which is the veritone structure. These functions reshape the data, sort by startTime/endTime,
   * add a client id (_cid) for DOM identification and tabindexing.
   */
  static transformTimelineMetadata(aiMetadata: HashMap, keyframeMetadata: KeyFrameMetadataEntry[]) {
    MetadataUtil.prev_Id = null;
    MetadataUtil.count = null;
    return MetadataUtil.transformAIMetadata(aiMetadata).concat(
      MetadataUtil.transformKeyframeMetadata(keyframeMetadata)
    );
  }

  static sortTimelineMetadataByName(metaData: AssetAiDataModel) {
    if (metaData)
      return _.sortBy(metaData, (meta) => {
        if (meta.label) {
          return meta.label;
        } else {
          return meta.groupName;
        }
      });
  }

  static sortTimelineMetadataByStartTime(metadata: AssetAiDataModel) {
    if (metadata)
      metadata.forEach((meta) => {
        meta.items = _.sortBy(meta.items, (e) => {
          return e.startTimeMs;
        });
      });
    return metadata;
  }

  static transformAIMetadata(aiMetadata: AssetAiDataModel) {
    MetadataUtil._colorCount = 0;
    // MetadataUtil._i = 0;
    const aiData = new Array();
    if (!aiMetadata) {
      return aiData;
    }
    aiMetadata.getGroupNames().map((name, i) => {
      const ids: Array<string> = aiMetadata.getAiDataIdsForGroupName(name);
      const aiDataForGroup = new Array();
      ids.forEach((id) => {
        const aiDataForId: AssetAiData = aiMetadata.getAssetAiData(id);
        const timecode: ?TimeCode = aiDataForId.getTimeCode();
        if (timecode) {
          aiDataForId.startTimeMs = timecode.getStartTime();
          aiDataForId.stopTimeMs = timecode.getEndTime();
        }
        aiDataForId.id = id;
        aiDataForId._boundingBox = aiDataForId.getBoundingBox();
        aiDataForId._cid = MetadataUtil._nextCid();
        aiDataForId._id = MetadataUtil.getUniqueId(id);
        aiDataForId.key = name;
        aiDataForId.isKeyFrameData = false;
        aiDataForId.value = aiDataForId.getValue() || '';
        aiDataForGroup.push(aiDataForId);
      });
      // "properties": aiDataForId.getProperties(), "value": aiDataForId.getProperties(), "confidence": aiDataForId.getProperties()
      if (aiDataForGroup.length) {
        aiData.push({
          key: name,
          groupName: name,
          label: name,
          color: MetadataUtil.nextTimelineColor(MetadataUtil._i),
          items: aiDataForGroup,
          isKeyFrameData: false
        });
      }
    });
    return aiData;
  }

  static transformKeyframeMetadata(metadata: KeyFrameMetadataEntry[]) {
    if (!metadata) {
      return [];
    }
    const keyFrameArray: Array = new Array();
    let objects = [];

    metadata.forEach((entry: KeyFrameMetadataEntry) => {
      try {
        entry.getMetadataMap().forEach((key: string, value: string) => {
          const o = {};
          o.key = key;
          o.value = value;
          o.id = entry.getId();
          o.startTimeMs = entry.getStartTime();
          o.stopTimeMs = entry.getEndTime();
          objects.push(o);
        });
      } catch (err) {
        debug('[transformKeyframeMetadata()] error processing entry: ', entry, err);
        return null;
      }
    });

    // const sortedItems = _.sortBy(objects, 'startTimeMs');
    const sortedItems = _.sortBy(objects, [
      function(o) {
        return o.id;
      }
    ]);

    sortedItems.forEach((obj, i) => {
      const isExits = keyFrameArray.findIndex(function(element) {
        return element.groupName === obj.key;
      });

      const keyFrameData = {
        startTimeMs: obj.startTimeMs,
        stopTimeMs: obj.stopTimeMs,
        id: obj.id,
        _cid: MetadataUtil._nextCid(),
        _id: MetadataUtil.getUniqueId(obj.id),
        value: obj.value,
        isKeyFrameData: true,
        key: obj.key
      };
      const keyFrameDataForGroup = new AssetAiKeyframeMetaData(keyFrameData);
      keyFrameDataForGroup.startTimeMs = obj.startTimeMs;
      keyFrameDataForGroup.stopTimeMs = obj.stopTimeMs;
      keyFrameDataForGroup.id = obj.id;
      keyFrameDataForGroup._cid = MetadataUtil._nextCid();
      keyFrameDataForGroup._id = MetadataUtil.getUniqueId(obj.id);
      keyFrameDataForGroup.value = obj.value;
      keyFrameDataForGroup.isKeyFrameData = true;
      keyFrameDataForGroup.key = obj.key;

      if (isExits === -1) {
        const items = [keyFrameDataForGroup];
        const propInfo = MetadataUtil.getPropInfo(obj.key);
        keyFrameArray.push({
          groupName: obj.key,
          key: obj.key,
          label: propInfo ? MetadataUtil.getPropInfo(obj.key).getKey() : '',
          color: MetadataUtil.nextTimelineColor(MetadataUtil._i),
          items: items,
          isKeyFrameData: true
        });
      } else {
        keyFrameArray[isExits].items.push(keyFrameDataForGroup);
      }
    });
    return keyFrameArray;
  }

  static getAiDataModelFromMetadataResponse(
    metadataResponse: GetMetadataResp,
    fuidRrnInfo: FuidRrnInfo
  ): ?AssetAiDataModel {
    const fuidBidRrnWithAssetAiData: Array<FuidBidRrnWithAssetAiData> = metadataResponse.getFuidBidRrnToAiDocumentIds();
    //metadataResponse.getFuidBidRrnToAiDocumentIds();
    const fuidBidRrnWithKeyFrameData: Array<FuidBidRrnWithAssetAiData> = metadataResponse.getFuid2keyFrameMetadataEntryList();
    let assetAiData: ?Array<AssetAiDataWebclient> = null;
    const groupNameToDocumentIds: Map<string, Array<string>> = new Map();
    if (fuidBidRrnWithAssetAiData) {
      for (let index = 0; index < fuidBidRrnWithAssetAiData.length; index++) {
        const item: FuidBidRrnWithAssetAiData = fuidBidRrnWithAssetAiData[index];
        const rxFuidRrnInfo: FuidRrnInfo = item.getFuidRrnInfo();
        if (rxFuidRrnInfo.getFuid() === fuidRrnInfo.getFuid() && rxFuidRrnInfo.getBid() === fuidRrnInfo.getBid()) {
          //&&
          //rxFuidRrnInfo.getRrn() === fuidRrnInfo.getRrn()
          const hashMap: HashMap = item.getGroupName2DocumentIds();
          if (hashMap) {
            hashMap.forEach(function(key: string, value: ValueList) {
              groupNameToDocumentIds.set(key, value.getValues());
            });
          }
          assetAiData = item.getAssetAiData();
          break;
        }
      }
    }

    if (fuidBidRrnWithKeyFrameData) {
    }
    if (assetAiData) {
      return new AssetAiDataModel(assetAiData, groupNameToDocumentIds);
    }
    return null;
  }
  static _colorCount = 0;
  static _i = 0;
  static nextTimelineColor(i: number = -1) {
    if (i === -1) {
      const color = MetadataUtil._timelineColors[MetadataUtil._timelineColorIndex];
      MetadataUtil._timelineColorIndex++;
      if (MetadataUtil._timelineColorIndex >= MetadataUtil._timelineColors.length) {
        MetadataUtil._timelineColorIndex = 0;
      }
      return color;
    } else {
      let color1 = null;
      if (i > 3 && MetadataUtil._colorCount === 4) {
        MetadataUtil._colorCount = 0;
        MetadataUtil._i = 0;
      }
      if (MetadataUtil._colorCount > 3) {
        MetadataUtil._colorCount = 0;
        MetadataUtil._i = 0;
      }
      color1 = MetadataUtil._timelineColors[MetadataUtil._colorCount];
      MetadataUtil._colorCount++;
      MetadataUtil._i++;
      return color1;
    }
  }

  static _timelineColors = ['#6174F2', '#F37937', '#685CEB', '#5CDFEB'];
  static _timelineColorIndex = 0;
  static _colorMap = {};
  static _cid = 1;
  static prev_Id = null;
  static count = 0;
  static getUniqueId(id) {
    if (MetadataUtil.prev_Id === id) {
      MetadataUtil.count++;
    } else {
      MetadataUtil.prev_Id = id;
      MetadataUtil.count = 1;
    }
    return id + '_' + MetadataUtil.count;
  }
  static _nextCid() {
    return MetadataUtil._cid++;
  }

  static isBlockMetadata = function(key) {
    if (MetadataUtil.BLOCALED_METADATA_LIST.includes(key)) return true;
    else return false;
  };

  static reporItemsToFuidRrnInfo = function(repoItems: Array<RepoItem>): Array<FuidRrnInfo> {
    const fuidRrnInfo = new Array();
    repoItems.forEach((repo: RepoItem) => {
      const itemFuidRrnInfo: FuidRrnInfo = new FuidRrnInfo();
      itemFuidRrnInfo.setFuid(repo.getFuid());
      itemFuidRrnInfo.setBid(repo.getBid());
      itemFuidRrnInfo.setRrn(repo.getRrn());
      fuidRrnInfo.push(itemFuidRrnInfo);
    });
    return fuidRrnInfo;
  };

  static reporItemToFuidRrnInfo = function(repoItem: RepoItem): FuidRrnInfo {
    const itemFuidRrnInfo: FuidRrnInfo = new FuidRrnInfo();
    itemFuidRrnInfo.setFuid(repoItem.getFuid());
    itemFuidRrnInfo.setBid(repoItem.getBid());
    itemFuidRrnInfo.setRrn(repoItem.getRrn());
    return itemFuidRrnInfo;
  };
}

type UpdateKeyframeMetadataObj = {
  stopTimeMs: string,
  stopTime: number,
  data: any,
  startTimeMs: string,
  startTime: number
};

export const updateKeyframeMetadata = async function(
  entryObj: Array<UpdateKeyframeMetadataObj>,
  asset: RepoItem,
  cb: function
) {
  try {
    const entries = new Array();
    entryObj.forEach(function(e) {
      const keyFrameEntry = new KeyFrameMetadataEntry();
      const { stopTimeMs, stopTime, data, startTimeMs, startTime } = e;
      const node = data;
      node[stopTimeMs] = stopTime;
      startTimeMs ? (node[startTimeMs] = startTime) : '';
      const map = new HashMap();
      map.put(node.key, node.value);
      keyFrameEntry
        .setId(node.id)
        .setStartTime(node.startTimeMs)
        .setEndTime(node.stopTimeMs)
        .setMetadataMap(map);
      if (stopTimeMs === 'startTimeMs') {
        keyFrameEntry.setStartTime(stopTime);
      } else if (stopTimeMs === 'stopTimeMs') {
        keyFrameEntry.setEndTime(stopTime);
      }
      if (startTimeMs && startTimeMs === 'startTimeMs') {
        keyFrameEntry.setStartTime(startTime);
      }
      entries.push(keyFrameEntry);
    });
    await MetadataUtil.editKeyFrameMetadata(asset, entries);
    cb();
    Store.put(ACTIVE_TIMELINE_NODE, null);
    Store.put(ACTIVE_TIMELINE_NODE, node);
  } catch (err) {
    debug('[UpdateKeyframeMetadata()] error: ', err);
    // toast(getErrorMessage(err));
  }
};
